import {useCallback, useMemo, useState} from "react";
import {Button, Snackbar, Typography} from "@mui/material";
import {useBrandingTitleSuffix} from "../../../../../../appfw/ui/branding/BrandingControl.tsx";
import DateRangeChooserStep from "./DateRangeChooserStep.tsx";
import RecipientsChooserStep from "./RecipientsChooserStep.tsx";
import {PreviewStep} from "./PreviewStep.tsx";
import {FormStep, StepForm} from "../../../../../../appfw/ui/forms/StepForm.tsx";
import FullSizeBox from "../../../../../../appfw/ui/components/FullSizeBox.tsx";
import {eventerConsoleApiClient} from "../../../../../api-client/EventerConsoleApiClient.ts";
import {useAccount} from "../../Accounts.tsx";

export interface SendEmailForm {
    readonly type: "info" | "updates"
    readonly startDateMin?: Date
    readonly startDateMax?: Date
    readonly dateModifiedMin?: Date
    readonly emailAddresses?: string[]
}

export default function SendEmailsPage() {

    useBrandingTitleSuffix("Email senden")

    const account = useAccount()
    const [formOpen, setFormOpen] = useState(false)

    const control = useState<SendEmailForm>({type: "info"})
    const [form, setForm] = control

    const [growlMessage, showGrowl] = useState<undefined | string>()

    const openForm = useCallback((type: "info" | "updates") => {
        setForm({type})
        setFormOpen(true)
    }, []);

    const finish = useCallback(async (cancel: boolean) => {

        let recipients = form.emailAddresses
        if (!cancel && recipients !== undefined && recipients.length > 0) {

            if (form.type === "info") {
                await eventerConsoleApiClient.sendInfoEmail({
                    accountId: account.id,
                    payload: {
                        emailAddresses: recipients,
                        startDateMin: form.startDateMin,
                        startDateMax: form.startDateMax,
                    }
                })
            } else {
                await eventerConsoleApiClient.sendUpdateEmail({
                    accountId: account.id,
                    payload: {
                        emailAddresses: recipients,
                        dateModifiedMin: form.dateModifiedMin,
                    }
                })
            }

            showGrowl(`E-Mail wurde an ${recipients.length} Empfänger versendet`)
        }

        setFormOpen(false)

    }, [form])

    const formSteps: FormStep<SendEmailForm>[] = useMemo(() => [
        {
            element: <DateRangeChooserStep control={control}/>,
            text: "Zeitraum",
            nextEnabled: (form) => {
                if (form.type === "info") {
                    return form.startDateMin !== undefined || form.startDateMax !== undefined
                }
                return form.dateModifiedMin !== undefined
            }
        }, {
            element: <RecipientsChooserStep control={control}/>,
            text: "Empfänger",
            nextEnabled: (form) => {
                return form.emailAddresses != undefined && form.emailAddresses.length > 0
            }
        }, {
            element: <PreviewStep form={form} finish={finish}/>,
            text: "Vorschau",
        }
    ], [form])

    if (formOpen) {
        return <StepForm steps={formSteps} form={form} finishText={"Senden"} finish={finish}/>
    }

    return (
        <FullSizeBox sx={{padding: 6, textAlign: "center"}}>
            <Button sx={{minWidth: 300}} variant="contained" onClick={() => openForm("info")}>
                Ankündigungen
            </Button>
            <Typography sx={{marginTop: 1}}>
                Übersicht zu Veranstaltungen für einen bestimmten Zeitraum
            </Typography>
            <Button sx={{minWidth: 300, marginTop: 8}} variant="contained" onClick={() => openForm("updates")}>
                Änderungsmitteilung
            </Button>
            <Typography sx={{marginTop: 1}}>
                Veranstaltungsänderungen seit einem bestimmten Datum
            </Typography>

            <Snackbar
                open={growlMessage !== undefined}
                autoHideDuration={4000}
                onClose={() => showGrowl(undefined)}
                message={growlMessage}
            />
        </FullSizeBox>
    )
}